<template>
	<div class="phone-input" :class="{ 'phone-input--success-icon': showSuccess && hasInteracted && isValid, 'phone-input--required': required }">
		<div class="phone-input__input-wrap" :class="{ 'phone-input__input-wrap--error': taken || shouldShowError }">
			<MazInputPhoneNumber
				:key="inputKey"
				v-model="phoneNumber"
				class="phone-input__input"
				:example="false"
				country-locale="pl-PL"
				:error="taken || shouldShowError"
				:translations="translations"
				:label="required ? $t('components.phone_input.phone_number') : undefined"
				show-code-on-list
				:orientation="isMobile_xs ? 'col' : 'row'"
				auto-format="typing"
				:disabled="disabled"
				country-code="PL"
				:class="{ 'phone-input--success': isValid }"
				:preferredCountries="['PL', 'GB', 'DE', 'NL', 'NO', 'IE', 'US', 'SE', 'BE', 'DK']"
				@data="results = $event"
				@blur="hasInteracted = true"
				@input="hasInteracted = true">
				<template #selector-flag="{ countryCode }">
					<div class="phone-input__flag phone-input__flag--selected">
						<GlobalIcon :height="28" prefix="country" :name="countryCode"></GlobalIcon>
					</div>
				</template>
				<template #country-list-flag="{ countryCode }">
					<div class="phone-input__flag">
						<GlobalIcon :height="28" prefix="country" :name="countryCode"></GlobalIcon>
					</div>
				</template>
			</MazInputPhoneNumber>
			<div v-if="showSuccess" class="phone-input__success">
				<GlobalIcon name="tick-circle" :height="18"></GlobalIcon>
			</div>
		</div>
		<div class="phone-input__error" :class="{ 'phone-input__error--empty': !shouldShowError || taken }">
			<TransitionGroup name="fade-down">
				<div v-if="shouldShowError && !taken" class="phone-input__error-message">
					{{ errorMessage }}
				</div>
			</TransitionGroup>
		</div>
	</div>
</template>
<script setup lang="ts">
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'

const phoneNumber = defineModel<string>()
const { isMobile_xs } = useWindowWidth()
const $t = useI18n().t
const results = ref<any>()
const hasInteracted = inject('hasInteracted', ref(false))

// MazInputPhoneNumber keeps its own internal state (selected country, formatted digits) and
// doesn't fully clear it when the v-model is programmatically reset to undefined/null after
// submit (it falls back to the stale internal value). Force a remount in that case only, so a
// user backspacing the field to '' while typing is left untouched.
const inputKey = ref(0)
watch(phoneNumber, (value, oldValue) => {
	if ((value === undefined || value === null) && oldValue) inputKey.value++
})
const emit = defineEmits<{
	(e: 'update', v: boolean): void
	(e: 'update:countryCode', v: string | undefined): void
}>()

const props = defineProps<{
	disabled?: boolean
	taken?: boolean
	customMessage?: string
	showSuccess?: boolean
	required?: boolean
}>()

const { required } = toRefs(props)

const isValid = computed(() => {
	if (props.taken) return false
	if (!results.value?.isValid) return false
	if (!phoneNumber.value) return false

	const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber.value)

	if (!parsedPhoneNumber) return false

	const type = parsedPhoneNumber.getType()

	if (type !== 'MOBILE') return false

	return true
})

const shouldShowError = computed(() => hasInteracted.value && !isValid.value)

const errorMessage = computed(() => {
	if (!results.value?.isValid) return props.customMessage ? props.customMessage : $t('validation.enter_valid_phone_number')

	const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber.value || '')
	if (parsedPhoneNumber?.getType() !== 'MOBILE') {
		return $t('validation.enter_mobile_number')
	}

	return props.customMessage ? props.customMessage : $t('validation.enter_valid_phone_number')
})

const translations = {
	countrySelector: {
		placeholder: $t('components.phone_input.prefix'),
		error: $t('components.phone_input.select_country'),
		searchPlaceholder: $t('components.phone_input.search'),
	},

	phoneInput: {
		placeholder: $t('components.phone_input.phone_number'),
		example: '___-___-___',
	},
}

watch(
	() => results.value,
	() => {
		if (results.value) {
			const { isValid, countryCode } = results.value
			emit('update', isValid)
			emit('update:countryCode', countryCode ? countryCode.toLowerCase() : undefined)
		}
	},
)
</script>

<style lang="scss" src="./PhoneInput.scss"></style>
